import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import HttpService from './services/HttpService';
import './CreateForm.css';
import moment from 'moment';

import { Layout, Row, Col, Table, Card, Empty, Spin } from 'antd';
import { InstagramOutlined, FacebookOutlined } from '@ant-design/icons';
import { brand } from './branding';

const { Header, Content } = Layout;

interface Data {
}

interface DataSourceItem {
  name: string;
  label: string;
}

const Home: React.FC = () => {
  const { code } = useParams();
  const [data, setData] = useState<Data | null>(null);
  const [dataSource, setDataSource] = useState<DataSourceItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const httpService = new HttpService();

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      setIsLoading(true);

      try {
        const response = await httpService.getSertifikasiByCode(code!);

        if (!isMounted) return;

        setData(response);

        const mappedDataSource = mapApiResponseToDataSource(response);
        setDataSource(mappedDataSource);
      } catch (error) {
        if (!isMounted) return;

        setData(null);
        setDataSource([]);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  const mapApiResponseToDataSource = (apiResponse: any): DataSourceItem[] => {
    const formatDate = (dateString: string | undefined): string => {
      return dateString ? moment(dateString).format('DD MMM YYYY') : '';
    };

    const formatPercentage = (value: string | undefined): string => {
      if (value && !isNaN(parseFloat(value))) {
        const percentage = (parseFloat(value) * 100).toFixed(2);
        return `${parseFloat(percentage)}%`;
      }
      return value || '';
    };

    const getYear = (date: string | undefined): string => {
      if(!date) return '';

      // Check if the date is in the ISO format
      const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/;
    
      if (isoDateRegex.test(date)) {
        // Extract and return the year from the ISO date
        return new Date(date).getUTCFullYear().toString();
      } else if (/^\d{4}$/.test(date)) {
        // Return the year if the date is already in the year format
        return date;
      } else {
        // Handle unexpected date formats if necessary
        throw new Error('Invalid date format');
      }
    };

    return [
      { name: apiResponse.sertifikasi.noBerkas, label: 'No Berkas' },
      { name: apiResponse.sertifikasi.noLot, label: 'No LOT' },
      { name: apiResponse.sertifikasi.noInduk, label: 'No. Induk' },
      { name: apiResponse.sertifikasi.produsen, label: 'Produsen' },
      { name: apiResponse.sertifikasi.alamat, label: 'Alamat' },
      { name: apiResponse.sertifikasi.jenisTanaman, label: 'Jenis Tanaman' },
      { name: apiResponse.sertifikasi.varietas, label: 'Varietas' },
      { name: apiResponse.sertifikasi.kelasBenih, label: 'Kelas Benih' },
      { name: formatDate(apiResponse.sertifikasi.tanggalPanen), label: 'Tanggal Panen' },
      { name: apiResponse.sertifikasi.beratKemasan, label: 'Berat Kemasan' },
      { name: apiResponse.sertifikasi.jumlahStokBenih || '', label: 'Jumlah Stok Benih' },
      { name: formatDate(apiResponse.sertifikasi.tanggalSelesaiUji), label: 'Tanggal Selesai Uji' },
      { name: formatDate(apiResponse.sertifikasi.tanggalAkhirMasaEdar), label: 'Tanggal Akhir Masa Edar' },
      { name: apiResponse.sertifikasi.noSeriAwal, label: 'No Seri Awal' },
      { name: apiResponse.sertifikasi.noSeriAkhir, label: 'No Seri Akhir' },
      { name: formatPercentage(apiResponse.sertifikasi.kadarAir), label: 'Kadar Air' },
      { name: formatPercentage(apiResponse.sertifikasi.dayaBerkecambah), label: 'Daya Berkecambah' },
      { name: formatPercentage(apiResponse.sertifikasi.benihMurni), label: 'Benih Murni' },
      { name: formatPercentage(apiResponse.sertifikasi.kotoranBenih), label: 'Kotoran Benih' },
      { name: formatPercentage(apiResponse.sertifikasi.cvlLapang), label: 'CVL Lapang' },
      { name: formatPercentage(apiResponse.sertifikasi.gulma), label: 'Benih Tanaman Lain/Gulma' },
      { name: apiResponse.sertifikasi.benihWarnaLain || '', label: 'Benih Warna Lain' },
      { name: getYear(apiResponse.sertifikasi.ta), label: 'TA' }
    ];
  };

  const handleInstagramClick = () => {
    const url = 'https://www.instagram.com/benihgiri?stkn=NWRtb2V4ZmViZXUw';
    window.open(url, '_blank');
  };

  const handleFacebookClick = () => {
    const url = 'https://www.facebook.com/share/1Hi77Y3UJA/?mibextid=wwXIfr';
    window.open(url, '_blank');
  };

  return (
    <div>
      <Layout>
        <Header className="public-header" style={{ backgroundColor: brand.colors.primary }}>
          <div style={{ display: 'flex', alignItems: 'center', color: '#fff', }}>
            <span style={{ marginRight: 10 }}>Follow Us: </span>
            <InstagramOutlined
              style={{ fontSize: 20, marginRight: 10, cursor: 'pointer' }}
              onClick={handleInstagramClick}
            />
            <FacebookOutlined style={{ fontSize: 20 }} onClick={handleFacebookClick} />
          </div>
        </Header>
      </Layout>

      {isLoading ? (
        <div className="certificate-loading-screen" role="status" aria-live="polite">
          <img className="certificate-loading-logo" src="/brand-mark.png" alt="" aria-hidden="true" />
          <Spin size="large" />
          <p>Memuat data sertifikasi...</p>
        </div>
      ) : data ? (
        <Content className="site-layout" style={{ marginTop: 20 }}>
          <div className="site-layout-background" style={{ padding: 24, minHeight: '650px', display: 'flex', justifyContent: 'center' }}>
            <Card className='custom-card'>
              <Row justify={'center'}>
                <img className="certificate-brand-logo" src="/brand-logo.png" alt="Logo PT GEMA INOVASI AGRI" />
              </Row>
              <Row justify={'center'}>
                <p style={{ fontSize: 20, textAlign: 'center', fontFamily: 'serif' }}>Informasi Label <br></br> Benih Bersertifikat</p>
              </Row>
              <Table
                pagination={false}
                size="middle"
                dataSource={dataSource}
                rowKey="name"
                style={{ marginBottom: 20, marginTop: 20 }}
                showHeader={false}
              >
                <Table.Column width='50%' align='left' title="Field" dataIndex="label" key="label" render={(text) => (
                  <span style={{ fontWeight: 'bold', fontSize: '16px' }}>{text}</span>
                )} />
                <Table.Column align='left' title="Input" dataIndex="name" key="name" render={(name) => (
                  <span style={{ fontSize: '16px' }}>: {name}</span>
                )} />
              </Table>
            </Card>
          </div>
        </Content>
      ) : <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '650px' }}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </div>
      }
    </div>
  );
}

export default Home;
